import { Injectable } from '@angular/core';
import {
  IMangoEventAnalyticsConfig,
  MangoEventAnalyticsType,
} from '../interface/mango-event-analytics.interface';

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

interface IsInitializedEventAnalytics {
  status: boolean;
  type: MangoEventAnalyticsType;
}

@Injectable({
  providedIn: 'root',
})
export class MangoEventAnalytics {
  private config: IMangoEventAnalyticsConfig[] = [];
  private initialized: IsInitializedEventAnalytics[] = [];

  constructor() {}

  // ================================
  // CONFIG
  // ================================
  setConfig(config: IMangoEventAnalyticsConfig[]) {
    this.config = config;

    // ❌ Evitar GA + GTM al mismo tiempo
    const hasGA = config.some(
      (c) => c.type === MangoEventAnalyticsType.GOOGLE_ANALYTICS
    );
    const hasGTM = config.some(
      (c) => c.type === MangoEventAnalyticsType.TAG_MANAGER
    );

    if (hasGA && hasGTM) {
      console.warn(
        '[MangoEventAnalytics] No se recomienda usar GOOGLE_ANALYTICS y TAG_MANAGER juntos'
      );
    }

    this.initializeAnalyticsConfig();
  }

  private initializeAnalyticsConfig() {
    this.config.forEach((config) => {
      switch (config.type) {
        case MangoEventAnalyticsType.GOOGLE_ANALYTICS:
          this.initializeGoogleAnalytics(config);
          break;

        case MangoEventAnalyticsType.TAG_MANAGER:
          this.initializeTagManager();
          break;
      }
    });
  }

  // ================================
  // GOOGLE ANALYTICS (GA4 DIRECTO)
  // ================================
  private initializeGoogleAnalytics(config: IMangoEventAnalyticsConfig) {
    if (this.isInitialized(MangoEventAnalyticsType.GOOGLE_ANALYTICS)) {
      console.warn('[GA] ya está inicializado');
      return;
    }

    // Inicializar dataLayer
    window.dataLayer = window.dataLayer || [];

    // Definir gtag
    window.gtag = function () {
      window.dataLayer.push(arguments);
    };

    // Cargar script gtag
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${config.key}`;
    document.head.appendChild(script);

    // Configuración GA
    window.gtag('js', new Date());
    window.gtag('config', config.key);

    this.markInitialized(MangoEventAnalyticsType.GOOGLE_ANALYTICS);

    console.log(`[GA] inicializado correctamente (${config.key})`);
  }

  // ================================
  // GOOGLE TAG MANAGER
  // ================================
  private initializeTagManager() {
    if (this.isInitialized(MangoEventAnalyticsType.TAG_MANAGER)) {
      return;
    }

    // ❗ GTM DEBE ESTAR CARGADO POR EL HOST (index.html)
    window.dataLayer = window.dataLayer || [];

    this.markInitialized(MangoEventAnalyticsType.TAG_MANAGER);

    console.log('[GTM] modo eventos habilitado');
  }

  // ================================
  // SEND EVENT
  // ================================
  sendEvent(event: string, data?: Record<string, any>) {
    this.initialized.forEach((analytics) => {
      if (!analytics.status) return;

      switch (analytics.type) {
        case MangoEventAnalyticsType.GOOGLE_ANALYTICS:
          this.sendGAEvent(event, data);
          break;

        case MangoEventAnalyticsType.TAG_MANAGER:
          this.sendGTMEvent(event, data);
          break;
      }
    });
  }

  // ================================
  // GA EVENT
  // ================================
  private sendGAEvent(event: string, data?: Record<string, any>) {
    if (typeof window.gtag === 'function') {
      window.gtag('event', event, data || {});
    } else {
      console.warn('[GA] gtag no está disponible');
    }
  }

  // ================================
  // GTM EVENT
  // ================================
  private sendGTMEvent(event: string, data?: Record<string, any>) {
    if (!Array.isArray(window.dataLayer)) {
      console.warn('[GTM] dataLayer no disponible');
      return;
    }

    window.dataLayer.push({
      event,
      ...(data || {}),
    });
  }

  // ================================
  // HELPERS
  // ================================
  private isInitialized(type: MangoEventAnalyticsType): boolean {
    return this.initialized.some((i) => i.type === type && i.status);
  }

  private markInitialized(type: MangoEventAnalyticsType) {
    this.initialized.push({ type, status: true });
  }
}
